import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Footer } from '../components/Footer';
import { ReservationModal } from '../components/ReservationModal';
import { Clock, Users, MapPin, ArrowLeft, Calendar, CheckCircle, Phone, AlertCircle } from 'lucide-react';

export default function GameDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [allReservations, setAllReservations] = useState([]);

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const gameDoc = await getDoc(doc(db, 'games', id));
        if (gameDoc.exists()) {
          const gameData = { id: gameDoc.id, ...gameDoc.data() };
          setGame(gameData);
          
          // Fetch branches for this game
          if (gameData.branches && gameData.branches.length > 0) {
            const branchesData = [];
            for (const branchId of gameData.branches) {
              const branchDoc = await getDoc(doc(db, 'branches', branchId));
              if (branchDoc.exists()) {
                branchesData.push({ id: branchDoc.id, ...branchDoc.data() });
              }
            }
            setBranches(branchesData);
          }
        }
      } catch (error) {
        console.error('Error fetching game:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [id]);

  // Real-time listener for reservations
  useEffect(() => {
    if (!id) return;

    const today = new Date().toISOString().split('T')[0];
    const q = query(
      collection(db, 'reservations'),
      where('gameId', '==', id),
      where('date', '>=', today),
      where('status', 'in', ['pending', 'confirmed'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.time.localeCompare(b.time);
      });
      setAllReservations(data);
    });

    return () => unsubscribe();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex flex-col items-center justify-center">
        <h1 className="text-3xl text-white mb-4">Oyun Tapılmadı</h1>
        <Button onClick={() => navigate('/games')} className="bg-[#D90429]">
          <ArrowLeft className="mr-2 w-4 h-4" />
          Oyunlara Qayıt
        </Button>
      </div>
    );
  }

  // Group reservations by date
  const reservationsByDate = allReservations.reduce((acc, res) => {
    if (!acc[res.date]) acc[res.date] = [];
    acc[res.date].push(res);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#050000] pt-20" data-testid="game-detail-page">
      {/* Hero */}
      <section className="relative h-[50vh] md:h-[60vh]">
        <div className="absolute inset-0">
          {game.imageUrl ? (
            <img
              src={game.imageUrl}
              alt={game.name}
              className="w-full h-full object-contain object-center bg-black"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-red-900/30 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050000] via-[#050000]/70 to-transparent" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/games')}
              className="text-white hover:bg-white/10 mb-3 sm:mb-4 text-sm sm:text-base"
            >
              <ArrowLeft className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
              Geri
            </Button>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4"
            >
              {game.name}
            </motion.h1>

            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 text-sm sm:text-base text-gray-300">
              {game.duration && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#D90429]" />
                  <span>{game.duration} dəqiqə</span>
                </div>
              )}
              {game.playerCount && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#D90429]" />
                  <span>{game.playerCount} oyunçu</span>
                </div>
              )}
              {game.price && game.showPrice !== false && (
                <div className="flex items-center gap-2 text-[#D90429] font-bold text-lg sm:text-xl">
                  {game.price} AZN
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
            {/* Story & Info */}
            <div className="lg:col-span-2 space-y-8 sm:space-y-10">
              {/* Description */}
              {(game.story || game.shortDescription) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-[#0F0F0F]/50 border border-white/5 rounded-xl p-6 sm:p-8"
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Hekayə</h2>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed whitespace-pre-line">
                    {game.story || game.shortDescription}
                  </p>
                </motion.div>
              )}

              {/* Video */}
              {game.videoUrl && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-[#0F0F0F]/50 border border-white/5 rounded-xl p-6 sm:p-8"
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Video</h2>
                  <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                    <video 
                      controls 
                      className="w-full h-full"
                      poster={game.imageUrl}
                    >
                      <source src={game.videoUrl} type="video/mp4" />
                    </video>
                  </div>
                </motion.div>
              )}

              {/* Gallery - No Title */}
              {game.images && game.images.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-[#0F0F0F]/50 border border-white/5 rounded-xl p-6 sm:p-8"
                >
                  <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                    {game.images.map((img, index) => (
                      <div 
                        key={index}
                        className="aspect-video bg-black rounded-lg overflow-hidden group cursor-pointer"
                      >
                        <img 
                          src={img} 
                          alt={`${game.name} - ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Available Branches */}
              {branches.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-[#0F0F0F]/50 border border-white/5 rounded-xl p-6 sm:p-8"
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Mövcud Filiallar</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {branches.map((branch) => (
                      <div 
                        key={branch.id}
                        className="bg-black/50 border border-white/10 rounded-lg p-4 sm:p-5 hover:border-red-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <MapPin className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <h3 className="text-white font-bold text-sm sm:text-base">{branch.name}</h3>
                        </div>
                        <p className="text-gray-400 text-xs sm:text-sm mb-3 pl-8">{branch.address}</p>
                        {branch.workingHours && (
                          <p className="text-gray-500 text-xs sm:text-sm mb-3 flex items-center gap-2 pl-8">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            {branch.workingHours}
                          </p>
                        )}
                        {branch.phones && branch.phones.length > 0 && (
                          <a 
                            href={`tel:${branch.phones[0].number}`}
                            className="text-red-400 text-xs sm:text-sm flex items-center gap-2 hover:text-red-300 pl-8"
                          >
                            <Phone className="w-4 h-4 flex-shrink-0" />
                            {branch.phones[0].number}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* All Reservations - Visible to everyone */}
              {allReservations.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-[#0F0F0F]/50 border border-white/5 rounded-xl p-6 sm:p-8"
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                    Rezervasiyalar ({allReservations.length})
                  </h2>
                  <p className="text-gray-400 text-xs sm:text-sm mb-6">
                    Aşağıdakı vaxtlar artıq rezerv edilib. Boş vaxt seçin.
                  </p>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {Object.entries(reservationsByDate).slice(0, 7).map(([date, reservations]) => (
                      <div key={date} className="bg-black/50 border border-white/10 rounded-lg p-4 sm:p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-red-500/10 rounded-lg px-3 py-2 text-center">
                            <div className="text-red-500 font-bold text-base sm:text-lg">{date.split('-')[2]}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(date).toLocaleDateString('az-AZ', { month: 'short', weekday: 'short' })}
                            </div>
                          </div>
                          <div className="text-white font-medium text-sm sm:text-base">
                            {reservations.length} rezervasiya
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                          {reservations.map((res) => (
                            <div
                              key={res.id}
                              className={`px-3 py-2 sm:px-4 sm:py-3 rounded-lg text-xs sm:text-sm ${
                                res.status === 'confirmed'
                                  ? 'bg-green-500/20 border border-green-500/30'
                                  : 'bg-yellow-500/20 border border-yellow-500/30'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {res.status === 'confirmed' ? (
                                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                                ) : (
                                  <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                                )}
                                <span className={res.status === 'confirmed' ? 'text-green-400 font-medium' : 'text-yellow-400 font-medium'}>
                                  {res.time}
                                </span>
                              </div>
                              <div className="text-gray-400 text-xs mt-1">
                                {res.branchName} • {res.playerCount} nəfər
                              </div>
                              <div className="text-gray-500 text-xs">
                                {res.status === 'confirmed' ? 'Təsdiqlənib' : 'Gözləyir'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Reservation Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24 p-6 bg-[#0F0F0F] border border-white/10 rounded-lg"
              >
                <h2 className="text-2xl font-bold text-white mb-4">Rezervasiya</h2>
                
                {branches.length === 0 ? (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Bu oyun heç bir filialda mövcud deyil</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-400 text-sm mb-6">
                      {branches.length} filialda mövcuddur
                    </p>

                    <Button
                      onClick={() => setReservationModalOpen(true)}
                      className="w-full bg-[#D90429] hover:bg-[#B00220] text-white py-6 text-lg font-bold"
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Rezerv Et
                    </Button>

                    {!user && (
                      <p className="text-gray-500 text-sm text-center mt-4">
                        Rezervasiya üçün{' '}
                        <button 
                          onClick={() => navigate('/login')}
                          className="text-[#D90429] hover:underline"
                        >
                          giriş edin
                        </button>
                      </p>
                    )}

                    {/* Quick Info */}
                    <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                      {game.duration && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Müddət</span>
                          <span className="text-white">{game.duration} dəqiqə</span>
                        </div>
                      )}
                      {game.playerCount && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Oyunçu</span>
                          <span className="text-white">{game.playerCount} nəfər</span>
                        </div>
                      )}
                      {game.price && game.showPrice !== false && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Qiymət</span>
                          <span className="text-red-500 font-bold">{game.price} AZN</span>
                        </div>
                      )}
                    </div>

                    {/* WhatsApp Contact */}
                    <div className="mt-6">
                      <a
                        href="https://api.whatsapp.com/send/?phone=994773243031"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        WhatsApp ilə Əlaqə
                      </a>
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Modal */}
      <ReservationModal
        game={game}
        isOpen={reservationModalOpen}
        onClose={() => setReservationModalOpen(false)}
      />

      <Footer />
    </div>
  );
}
